import {useEffect, useState} from "react";
import {connect, useDispatch, useSelector} from "react-redux";
import {Box, Tabs, Tab} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from '@mui/material/FormControlLabel';
import {change, Field, reduxForm, reset, untouch} from "redux-form";
import _ from "lodash";
import {userInviteActions} from "_actions";
import {isValidEmail, required} from "_helpers";
import ActionButton from "../../../common/ActionButton";
import AnchorButton from "../../../common/AnchorButton";
import BasicFormTextField from "../../../common/BasicFormTextField";
import OrganizationSelect from "../../../common/OrganizationSelect";
import RoleSelect from "../../../common/RoleSelect";
import {SnackBar} from "../../../../common";
import {buildCreatePayload, rolesForOrganization} from "../Utilities/UserInviteUtils";

function CreateUserInviteForm({handleSubmit}) {

    const [organizationEnabled, setOrganizationEnabled] = useState(null);
    const [activeTab, setActiveTab] = useState('new');
    const dispatch = useDispatch();
    const selectPlaceholder = 'Choose';

    const userInvite = useSelector(state => state.userInvite.userInvite);
    const loading = useSelector(state => state.userInvite.loadingCreate);
    const roles = useSelector(state => state.roles.roles);
    const filteredRoleIds = roles?.roles
        ?.filter(role => rolesForOrganization.includes(role.authority))
        .map(role => role.id);

    const selectedOrganization = useSelector(
        state => state.form?.CreateUserInviteForm?.values?.organization
    );

    useEffect(() => {
        if (!organizationEnabled) {
            dispatch(change('CreateUserInviteForm', 'organization', undefined));
        }
    }, [organizationEnabled, dispatch]);

    useEffect(() => {
        if (userInvite && !loading) {
            dispatch(reset('CreateUserInviteForm'));
            setOrganizationEnabled(null);
        }
    }, [userInvite, loading, dispatch]);

    const handleSelectChange = (event) => {
        if (event.target.name === 'role') {
            const isIncluded = filteredRoleIds?.includes(event.target.value);
            setOrganizationEnabled(isIncluded);
        }
    };

    const handleOrganizationSelectChange = (event) => {
        dispatch(change('CreateUserInviteForm','organization', event.target.value));
    }

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        dispatch(reset('CreateUserInviteForm'));
        setOrganizationEnabled(null);
    };

    const handleCreate = (values) => {
        if(_.isEmpty(values)) return;

        if(!isValidEmail(values?.email)) {
            SnackBar.showMessage('Invalid email');
            return;
        }
        const payload = buildCreatePayload(values);
        dispatch(userInviteActions.create(payload));
    };

    const renderCheckbox = ({ input, label }) => (
      <FormControlLabel
        control={
          <Checkbox
            checked={input.checked ? true : false}
            onChange={input.onChange}
          />
        }
        label={label}
      />
    );

    return(
        <>
            <form name="form" onSubmit={handleSubmit(handleCreate)} autoComplete="off">
                <Box sx={{ borderBottom: '1px solid #CED4D4', px: 2 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                    >
                        <Tab label="New User" value="new" />
                        <Tab label="Existing User" value="existing" />
                    </Tabs>
                </Box>

                <Box sx={{padding: '20px 20px 40px 20px'}}>
                    {activeTab === 'new' && (
                        <>
                            <Box mb={{xs: '10px', sm: '10px', md: '15px', lg: '15px'}}>
                                <Box sx={styles.inputDesc}>Email</Box>
                                <Field
                                    name="email"
                                    component={BasicFormTextField}
                                    placeholder={'Enter email'}
                                    validate={required}
                                />
                            </Box>
                            <Box mb={{xs: '10px', sm: '10px', md: '15px', lg: '15px'}}>
                                <Box sx={styles.inputDesc}>First Name</Box>
                                <Field
                                    name="firstName"
                                    component={BasicFormTextField}
                                    placeholder={'Enter first name'}
                                    validate={required}
                                />
                            </Box>
                            <Box mb={{xs: '10px', sm: '10px', md: '15px', lg: '15px'}}>
                                <Box sx={styles.inputDesc}>Last Name</Box>
                                <Field
                                    name="lastName"
                                    component={BasicFormTextField}
                                    placeholder={'Enter last name'}
                                    validate={required}
                                />
                            </Box>
                            <Box mb={{xs: '10px', sm: '10px', md: '15px', lg: '15px'}}>
                                <Box sx={styles.inputDesc}>Role</Box>
                                <Field
                                    name="role"
                                    component={RoleSelect}
                                    placeholder={selectPlaceholder}
                                    handleChange={handleSelectChange}
                                />
                            </Box>
                            <Box  mb={{xs: '10px', sm: '10px', md: '15px', lg: '15px'}}>
                                <Box sx={styles.inputDesc}>Organization</Box>
                                <Field
                                    name="organization"
                                    component={OrganizationSelect}
                                    placeholder={selectPlaceholder}
                                    input={{value: selectedOrganization, disabled: !organizationEnabled}}
                                    handleChange={handleOrganizationSelectChange}
                                />
                            </Box>
                        </>
                    )}

                    {activeTab === 'existing' && (
                        <>
                            <Box mb={{xs: '10px', sm: '10px', md: '15px', lg: '15px'}}>
                                <Box sx={styles.inputDesc}>Email</Box>
                                <Field
                                    name="email"
                                    component={BasicFormTextField}
                                    placeholder={'Enter email'}
                                    validate={required}
                                />
                            </Box>
                            <Box  mb={{xs: '10px', sm: '10px', md: '15px', lg: '15px'}}>
                                <Box sx={styles.inputDesc}>Role</Box>
                                <Field
                                    name="role"
                                    component={RoleSelect}
                                    placeholder={selectPlaceholder}
                                    handleChange={handleSelectChange}
                                />
                            </Box>
                            <Box mb={{xs: '10px', sm: '10px', md: '15px', lg: '15px'}}>
                                <Box sx={styles.inputDesc}>Organization</Box>
                                <Field
                                    name="organization"
                                    component={OrganizationSelect}
                                    placeholder={selectPlaceholder}
                                    input={{value: selectedOrganization, disabled: !organizationEnabled}}
                                    handleChange={handleOrganizationSelectChange}
                                />
                            </Box>
                            <Box mb={{xs: '10px', sm: '10px', md: '15px', lg: '15px'}}>
                                <Box sx={styles.inputDesc}>Auto-Accept</Box>
                                <Field
                                    name="autoAccept"
                                    component={renderCheckbox}
                                    type="checkbox"
                                    label="Accept the invite automatically"
                                />
                            </Box>
                        </>
                    )}
                </Box>

                <Box sx={styles.footer}>
                    <Box sx={{}}>
                        <ActionButton
                            label={'Send invite'}
                            disabled={loading}
                        />
                    </Box>
                    <Box sx={{width: '100px', ml: '18px', height: '20px'}}>
                        <AnchorButton
                            label={'Cancel'}
                            link={'/users/invites'}
                            buttonBordered={true}
                        />
                    </Box>
                </Box>
            </form>
        </>
    )
}

const styles = {
    inputDesc: {
        fontFamily: 'LarsseitMedium',
        fontSize: '14px',
        fontWeight: 500,
        color: '#495D5E',
        mb: '7px'
    },
    footer: {
        borderTop: '1px solid #CED4D4',
        padding: '12px 20px',
        display: 'flex'
    },
}


function makeValidate(roles) {
    const roleIdsRequiringOrg = roles
        ?.filter(role => rolesForOrganization.includes(role.authority))
        .map(role => role.id);

    return function validate(values) {
        const errors = {};
        const { organization, role } = values;

        const roleRequiresOrg = roleIdsRequiringOrg?.includes(role);

        if (_.isEmpty(_.toString(role)) || role === 'none') {
            errors.role = 'invalid role';
        }

        if (roleRequiresOrg && (_.isEmpty(_.toString(organization)) || organization === 'none')) {
            errors.organization = 'invalid organization';
        }

        return errors;
    };
}

const mapStateToProps = (state) => {
    const roles = state.roles.roles?.roles || [];
    return {
        validate: makeValidate(roles),
    };
};

CreateUserInviteForm = reduxForm({
    form: 'CreateUserInviteForm',
})(CreateUserInviteForm);

export default connect(mapStateToProps)(CreateUserInviteForm);
